import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatUSD, formatDate, formatApiError, todayISO } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Combobox } from "@/components/ui/combobox";
import { Plus, Pencil, Trash2, Play, Repeat } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

const FREQS = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

const PAYMENT_METHODS = ["Bank Transfer", "Cash", "Check", "Credit/Debit Card", "Online", "Other"];

function RecurringDialog({
  open,
  onOpenChange,
  orgId,
  item,
  categories: initialCategories,
  donors: initialDonors,
  funds: initialFunds,
  vendors: initialVendors,
  origins: initialOrigins,
  onSaved,
  onLookupsChanged,
}) {
  const isEdit = !!item;
  const [categories, setCategories] = useState(initialCategories);
  const [donors, setDonors] = useState(initialDonors);
  const [funds, setFunds] = useState(initialFunds);
  const [vendors, setVendors] = useState(initialVendors || []);
  const [origins, setOrigins] = useState(initialOrigins || []);

  const blankForm = {
    name: "",
    type: "income",
    amount: "",
    category_id: "",
    frequency: "monthly",
    start_date: todayISO(),
    end_date: "",
    donor_id: "",
    fund_id: "",
    vendor_id: "",
    payment_method: "",
    origin: "",
    description: "",
    tax_deductible: true,
    active: true,
  };
  const [form, setForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setCategories(initialCategories); }, [initialCategories]);
  useEffect(() => { setDonors(initialDonors); }, [initialDonors]);
  useEffect(() => { setFunds(initialFunds); }, [initialFunds]);
  useEffect(() => { setVendors(initialVendors || []); }, [initialVendors]);
  useEffect(() => { setOrigins(initialOrigins || []); }, [initialOrigins]);

  useEffect(() => {
    if (item) {
      setForm({
        name: item.name || "",
        type: item.type || "income",
        amount: String(item.amount ?? ""),
        category_id: item.category_id || "",
        frequency: item.frequency || "monthly",
        start_date: item.start_date || todayISO(),
        end_date: item.end_date || "",
        donor_id: item.donor_id || "",
        fund_id: item.fund_id || "",
        vendor_id: item.vendor_id || "",
        payment_method: item.payment_method || "",
        origin: item.origin || "",
        description: item.description || "",
        tax_deductible: item.tax_deductible !== false,
        active: item.active !== false,
      });
    } else if (open) {
      setForm(blankForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, open]);

  const cats = useMemo(() => categories.filter((c) => c.type === form.type), [categories, form.type]);

  const createCategoryInline = async (name) => {
    try {
      const { data } = await api.post(`/organizations/${orgId}/categories`, { name: name.trim(), type: form.type });
      const newCat = { id: data.id, name: data.name, type: form.type };
      setCategories((arr) => [...arr, newCat]);
      onLookupsChanged?.();
      toast.success(`Created ${form.type} category "${newCat.name}"`);
      return { value: newCat.id, label: newCat.name };
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const createFundInline = async (name) => {
    try {
      const { data } = await api.post(`/organizations/${orgId}/funds`, { name: name.trim() });
      const newFund = { id: data.id, name: data.name };
      setFunds((arr) => [...arr, newFund]);
      onLookupsChanged?.();
      toast.success(`Created fund "${newFund.name}"`);
      return { value: newFund.id, label: newFund.name };
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const createVendorInline = async (name) => {
    try {
      const { data } = await api.post(`/organizations/${orgId}/vendors`, { name: name.trim() });
      const newV = { id: data.id, name: data.name };
      setVendors((arr) => [...arr, newV]);
      onLookupsChanged?.();
      toast.success(`Created vendor "${newV.name}"`);
      return { value: newV.id, label: newV.name };
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const createDonorInline = async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    const parts = trimmed.split(/\s+/);
    const first_name = parts[0];
    const last_name = parts.length > 1 ? parts[parts.length - 1] : "";
    const middle_name = parts.length > 2 ? parts.slice(1, -1).join(" ") : "";
    try {
      const { data } = await api.post(`/organizations/${orgId}/donors`, {
        first_name, middle_name, last_name, active: true,
      });
      const newDonor = { id: data.id, name: data.name || trimmed, active: true };
      setDonors((arr) => [...arr, newDonor]);
      onLookupsChanged?.();
      toast.success(`Created donor "${newDonor.name}"`);
      return { value: newDonor.id, label: newDonor.name };
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const createOriginInline = async (name) => {
    try {
      const { data } = await api.post(`/organizations/${orgId}/donation-origins`, { name: name.trim() });
      const newO = { id: data.id, name: data.name };
      setOrigins((arr) => [...arr, newO]);
      onLookupsChanged?.();
      toast.success(`Created origin "${newO.name}"`);
      return { value: newO.name, label: newO.name };
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.category_id) return toast.error("Please choose a category");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Amount must be positive");
    if (!form.start_date) return toast.error("Start date is required");
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        amount: Number(form.amount),
        category_id: form.category_id,
        frequency: form.frequency,
        start_date: form.start_date,
        end_date: form.end_date || null,
        donor_id: form.type === "income" && form.donor_id ? form.donor_id : null,
        fund_id: form.fund_id || null,
        vendor_id: form.type === "expense" && form.vendor_id ? form.vendor_id : null,
        payment_method: form.payment_method || null,
        origin: form.type === "income" && form.origin ? form.origin : null,
        description: form.description || "",
        tax_deductible: form.type === "income" ? !!form.tax_deductible : false,
        active: !!form.active,
      };
      if (isEdit) await api.patch(`/organizations/${orgId}/recurring/${item.id}`, payload);
      else await api.post(`/organizations/${orgId}/recurring`, payload);
      toast.success(isEdit ? "Recurring rule updated" : "Recurring rule created");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const showDonor = form.type === "income";
  const showVendor = form.type === "expense";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="recurring-dialog" className="rounded-3xl max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">
            {isEdit ? "Edit recurring rule" : "New recurring rule"}
          </DialogTitle>
          <DialogDescription>
            Schedule repeating income (recurring tithes) or expenses (rent, salaries, utilities) and post them with one click.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {!isEdit && (
            <ToggleGroup
              type="single"
              value={form.type}
              onValueChange={(v) => v && setForm((f) => ({ ...f, type: v, category_id: "", donor_id: "", vendor_id: "", origin: "" }))}
              className="grid grid-cols-2 gap-2"
            >
              <ToggleGroupItem
                value="income"
                data-testid="recurring-type-income"
                className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground border border-border h-11 rounded-xl"
              >
                Recurring Donation / Income
              </ToggleGroupItem>
              <ToggleGroupItem
                value="expense"
                data-testid="recurring-type-expense"
                className="data-[state=on]:bg-expense data-[state=on]:text-expense-foreground border border-border h-11 rounded-xl"
              >
                Recurring Expense
              </ToggleGroupItem>
            </ToggleGroup>
          )}

          <div className="space-y-1.5">
            <Label>Name <span className="text-destructive">*</span></Label>
            <Input
              data-testid="recurring-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={form.type === "income" ? "e.g. Smith Family Monthly Tithe" : "e.g. Monthly Office Rent"}
              className="rounded-xl"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Fund</Label>
              <Combobox
                value={form.fund_id}
                onValueChange={(v) => setForm({ ...form, fund_id: v })}
                items={funds.map((f) => ({ value: f.id, label: f.name }))}
                placeholder="Select or create fund…"
                searchPlaceholder="Search funds…"
                emptyMessage="No funds. Type a name to create one."
                onCreate={createFundInline}
                testid="recurring-fund-select"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Category <span className="text-destructive">*</span></Label>
              <Combobox
                value={form.category_id}
                onValueChange={(v) => setForm({ ...form, category_id: v })}
                items={cats.map((c) => ({ value: c.id, label: c.name }))}
                placeholder="Select or create category…"
                searchPlaceholder={`Search ${form.type} categories…`}
                emptyMessage="No matching category. Type a name to create one."
                onCreate={createCategoryInline}
                testid="recurring-category-select"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Amount <span className="text-destructive">*</span></Label>
              <Input
                data-testid="recurring-amount-input"
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="rounded-xl tabular-nums"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Frequency <span className="text-destructive">*</span></Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="recurring-frequency-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Start date <span className="text-destructive">*</span></Label>
              <Input
                type="date"
                data-testid="recurring-start-date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label>End date (optional)</Label>
              <Input
                type="date"
                data-testid="recurring-end-date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="rounded-xl"
              />
            </div>
          </div>

          {showDonor && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Donor</Label>
                <Combobox
                  value={form.donor_id}
                  onValueChange={(v) => setForm({ ...form, donor_id: v })}
                  items={donors.filter((d) => d.active !== false).map((d) => ({ value: d.id, label: d.name }))}
                  placeholder="Select or create donor…"
                  searchPlaceholder="Search donors…"
                  emptyMessage="No matching donor. Type a name to create one."
                  onCreate={createDonorInline}
                  testid="recurring-donor-select"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Origin of donation</Label>
                <Combobox
                  value={form.origin}
                  onValueChange={(v) => setForm({ ...form, origin: v })}
                  items={origins.map((o) => ({ value: o.name, label: o.name }))}
                  placeholder="Select or create origin…"
                  searchPlaceholder="Search origins…"
                  emptyMessage="No matching origin."
                  onCreate={createOriginInline}
                  testid="recurring-origin-select"
                />
              </div>
            </div>
          )}

          {showVendor && (
            <div className="space-y-1.5">
              <Label>Vendor / Payee</Label>
              <Combobox
                value={form.vendor_id}
                onValueChange={(v) => setForm({ ...form, vendor_id: v })}
                items={vendors.map((v) => ({ value: v.id, label: v.name }))}
                placeholder="Select or enter vendor…"
                searchPlaceholder="Search or type a new vendor…"
                emptyMessage="No matching vendor. Type a name to create one."
                onCreate={createVendorInline}
                testid="recurring-vendor-select"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Payment method</Label>
            <Select
              value={form.payment_method || "_none"}
              onValueChange={(v) => setForm({ ...form, payment_method: v === "_none" ? "" : v })}
            >
              <SelectTrigger data-testid="recurring-payment-method-select" className="rounded-xl">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">—</SelectItem>
                {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              data-testid="recurring-description-input"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional notes — added to each generated transaction"
              className="rounded-xl"
            />
          </div>

          <div className="flex flex-wrap items-center gap-6 pt-1">
            {showDonor && (
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.tax_deductible}
                  onCheckedChange={(v) => setForm({ ...form, tax_deductible: v })}
                  data-testid="recurring-tax-deductible-switch"
                />
                <span className="text-sm font-medium">Tax deductible</span>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Switch
                data-testid="recurring-active-switch"
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
              />
              <span className="text-sm font-medium">Active</span>
              <span className="text-xs text-muted-foreground">(inactive rules don't post)</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="rounded-xl soft-glow" data-testid="recurring-save-button">
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Create rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RecurringPage() {
  const { activeOrgId } = useAuth();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [donors, setDonors] = useState([]);
  const [funds, setFunds] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const fetchAll = async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const [r, c, d, f, v, o] = await Promise.all([
        api.get(`/organizations/${activeOrgId}/recurring`),
        api.get(`/organizations/${activeOrgId}/categories`),
        api.get(`/organizations/${activeOrgId}/donors`),
        api.get(`/organizations/${activeOrgId}/funds`),
        api.get(`/organizations/${activeOrgId}/vendors`),
        api.get(`/organizations/${activeOrgId}/donation-origins`),
      ]);
      setItems(r.data);
      setCats(c.data);
      setDonors(d.data);
      setFunds(f.data);
      setVendors(v.data);
      setOrigins(o.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [activeOrgId]);

  const postNow = async (it) => {
    try {
      await api.post(`/organizations/${activeOrgId}/recurring/${it.id}/post`);
      toast.success("Posted to ledger");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async () => {
    if (!confirmTarget) return;
    try {
      await api.delete(`/organizations/${activeOrgId}/recurring/${confirmTarget.id}`);
      toast.success("Recurring rule deleted");
      setConfirmTarget(null);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); setConfirmTarget(null); }
  };

  return (
    <div className="space-y-8 fade-up">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Recurring</div>
          <h1 className="font-serif text-5xl tracking-tight mt-2">Repeating income & expenses</h1>
          <p className="text-muted-foreground mt-2 max-w-md">
            Define the rhythm — rent, salaries, recurring gifts — and post them with one click.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="h-11 rounded-xl" data-testid="add-recurring-button">
          <Plus className="w-4 h-4 mr-2" /> New recurring
        </Button>
      </header>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <Card className="border-border shadow-none p-12 text-center rounded-3xl">
          <Repeat className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-serif text-2xl mt-4">No recurring rules yet</div>
          <p className="text-muted-foreground mt-2">Add tithes, rent, salaries or grants on a schedule.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((it) => (
            <Card key={it.id} className="border-border shadow-none p-5 rounded-2xl transition-all duration-200 hover:shadow-md" data-testid={`recurring-card-${it.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={it.type === "income" ? "bg-primary/10 text-primary border-primary/20" : "bg-expense/10 text-expense border-expense/20"}>{it.type}</Badge>
                    {!it.active && <Badge variant="outline" className="bg-muted text-muted-foreground">Paused</Badge>}
                    {it.fund_name && <Badge variant="outline" className="bg-secondary/60">{it.fund_name}</Badge>}
                  </div>
                  <div className="font-serif text-2xl mt-3 truncate">{it.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {it.category_name} · {it.frequency} · starts {formatDate(it.start_date)}
                  </div>
                  {(it.donor_name || it.vendor_name || it.payment_method) && (
                    <div className="text-xs text-muted-foreground mt-1.5 truncate">
                      {it.donor_name && <span>Donor: <span className="text-foreground">{it.donor_name}</span></span>}
                      {it.vendor_name && <span>Payee: <span className="text-foreground">{it.vendor_name}</span></span>}
                      {it.payment_method && <span> · {it.payment_method}</span>}
                    </div>
                  )}
                </div>
                <div className={`font-mono text-xl tabular-nums ${it.type === "income" ? "text-primary" : "text-expense"}`}>
                  {formatUSD(it.amount)}
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  Next: <span className="font-medium text-foreground tabular-nums">{formatDate(it.next_run_date)}</span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => postNow(it)} data-testid={`recurring-post-${it.id}`}>
                    <Play className="w-4 h-4 mr-1" /> Post now
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(it); setDialogOpen(true); }} data-testid={`recurring-edit-${it.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setConfirmTarget(it)} data-testid={`recurring-delete-${it.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <RecurringDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        orgId={activeOrgId}
        item={editing}
        categories={cats}
        donors={donors}
        funds={funds}
        vendors={vendors}
        origins={origins}
        onSaved={fetchAll}
        onLookupsChanged={fetchAll}
      />

      <ConfirmDialog
        open={!!confirmTarget}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title={`Delete "${confirmTarget?.name}"?`}
        description="This will remove the recurring schedule. Already-posted transactions remain in the ledger."
        confirmLabel="Delete rule"
        onConfirm={doDelete}
        testid="confirm-delete-recurring"
      />
    </div>
  );
}
